import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Portal,
  CloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Flex,
  IconButton,
  Image
} from '@chakra-ui/react';
import {
  LuArrowUp,
  LuArrowDown,
  LuGripVertical,
  LuTrash2
} from 'react-icons/lu';
import { COLORS } from '../../../theme';

export default function ReorderGalleryImages({
  isOpen,
  onClose,
  images,
  onSave
}) {
  const [localImages, setLocalImages] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLocalImages([...images]);
    }
  }, [isOpen, images]);

  const moveItem = (index, direction) => {
    const updated = [...localImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= updated.length) return;

    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setLocalImages(updated);
  };

  const removeItem = (index) => {
    setLocalImages(localImages.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave(localImages);
    onClose();
  };

  const borderColor = { _light: 'gray.200', _dark: 'gray.700' };
  const itemBg = { _light: 'white', _dark: 'gray.900' };
  const hoverBg = { _light: 'gray.50', _dark: 'gray.800' };

  return (
    <Dialog.Root
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner shadow="2xl">
          <Dialog.Content maxH="80vh">
            <Dialog.Header borderBottomWidth="1px" borderColor={borderColor}>
              <Dialog.Title>Reorder Gallery Images</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={4}>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Change the order of images in this gallery. The first image will
                be used as the cover.
              </Text>
              <VStack gap={2} align="stretch">
                {localImages.map((url, index) => (
                  <Flex
                    key={`${url}-${index}`}
                    p={2}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    align="center"
                    bg={itemBg}
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Box color="gray.400" mr={3}>
                      <LuGripVertical />
                    </Box>
                    <Image
                      src={url}
                      alt={`Image ${index}`}
                      w="50px"
                      h="50px"
                      borderRadius="sm"
                      objectFit="cover"
                      mr={3}
                    />
                    <Box flex={1} overflow="hidden">
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        noOfLines={1}
                        title={url}
                      >
                        {url}
                      </Text>
                    </Box>
                    <HStack gap={1} ml={4}>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        aria-label="Move Up"
                      >
                        <LuArrowUp />
                      </IconButton>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === localImages.length - 1}
                        aria-label="Move Down"
                      >
                        <LuArrowDown />
                      </IconButton>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => removeItem(index)}
                        aria-label="Remove Image"
                      >
                        <LuTrash2 />
                      </IconButton>
                    </HStack>
                  </Flex>
                ))}
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" borderColor={borderColor}>
              <Flex gap={3} justify="flex-end" w="full">
                <Button onClick={onClose} variant="ghost" size="sm">
                  Cancel
                </Button>
                <Button
                  colorPalette="orange"
                  bg={COLORS.brand.primary}
                  _hover={{ bg: COLORS.brand.hover }}
                  onClick={handleSave}
                  size="sm"
                  px={6}
                >
                  Apply Order
                </Button>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
